import { useEffect, useRef, useState } from 'react'
import { Plus, MoreHorizontal } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUpdateChatTitle } from '@/hooks'
import { BorderColorIcon, DeleteIcon, SearchIcon } from '@/components/icons'
import { cn } from '@/lib/utils'
import { DeleteChatDialog } from './DeleteChatDialog'
import type { Chat } from '@/types'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { WorkflowStatusBadge } from '../workflows/shared/WorkflowStatusBadge'

interface ChatListSidebarProps {
  projectId: string
  chats: Chat[]
  activeChatId: string | null
  onSelectChat: (chatId: string) => void
  onCreateChat: () => void
  onDeleteChat: (chatId: string) => void
  isCreating: boolean
  searchTerm: string
  onSearchChange: (value: string) => void
}

export function ChatListSidebar({
  projectId,
  chats,
  activeChatId,
  onSelectChat,
  onCreateChat,
  onDeleteChat,
  isCreating,
  searchTerm,
  onSearchChange,
}: Readonly<ChatListSidebarProps>) {
  const [editingChatId, setEditingChatId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [chatToDelete, setChatToDelete] = useState<Chat | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const updateTitle = useUpdateChatTitle(projectId)

  const handleRenameStart = (chat: Chat) => {
    setEditingChatId(chat.id)
    setEditTitle(chat.title)
  }

  const handleRenameConfirm = async (chatId: string) => {
    if (!editTitle.trim()) {
      toast.error('Chat name cannot be empty')
      return
    }
    try {
      await updateTitle.mutateAsync({ chatId, title: editTitle.trim() })
      setEditingChatId(null)
    } catch {
      toast.error('Failed to rename chat')
    }
  }

  const handleRenameCancel = () => {
    setEditingChatId(null)
    setEditTitle('')
  }

  const handleDeleteStart = (chat: Chat) => {
    setChatToDelete(chat)
    setDeleteDialogOpen(true)
  }

  const isActive = (chatId: string) => activeChatId === chatId

  return (
    <div className="flex h-full flex-col border rounded-xl bg-panel">
      <div className="py-3 px-4 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">All Chats</h3>
          <Button variant="ghost" size="sm" className="-mr-2" onClick={onCreateChat} disabled={isCreating} aria-label="Create new chat">
            <Plus className="h-5 w-5" />
          </Button>
        </div>
        <div className="">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-foreground" />
            <Input
              placeholder="Search"
              className="pl-9 text-sm leading-[1.6] tracking-[0.015em] placeholder:text-border"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label="Search chats"
              data-testid="chat-search-input"
            />
          </div>
        </div>
      </div>
      <TooltipProvider>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {chats.length === 0 && (
          <p data-testid="chat-list-empty-state" className="px-2 py-4 text-center text-sm text-muted-foreground">
            {searchTerm ? 'No chats found.' : 'No chats yet. Click + to start.'}
          </p>
        )}
        {chats.map((chat) => (
          <div
            key={chat.id}
            data-testid={`chat-item-${chat.id}`}
            aria-current={isActive(chat.id) ? 'page' : undefined}
            className={cn(
              'group flex items-center gap-2 rounded-md px-3 py-2 text-sm cursor-pointer transition-colors',
              editingChatId === chat.id
                ? 'bg-muted text-foreground'
                : isActive(chat.id)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
            )}
            onClick={() => editingChatId !== chat.id && onSelectChat(chat.id)}
          >
            {editingChatId === chat.id ? (
              <>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameConfirm(chat.id)
                    if (e.key === 'Escape') handleRenameCancel()
                  }}
                  onBlur={() => handleRenameCancel()}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  maxLength={100}
                  aria-label="Rename chat"
                  className="h-6 flex-1 min-w-0 border-none bg-transparent px-1 py-0 text-sm text-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid={`rename-input-${chat.id}`}
                />
              </>
            ) : (
              <>
                <ChatTitle title={chat.title} />
                {(chat.hasPendingApprovalChanges || chat.latestActiveRunStatus) && (
                  <WorkflowStatusBadge
                    status={chat.hasPendingApprovalChanges ? 'AwaitingApproval' : chat.latestActiveRunStatus!}
                    iconOnly
                    onPrimary={isActive(chat.id)}
                    className="shrink-0"
                  />
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={cn(
                        'shrink-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity focus:outline-none',
                        isActive(chat.id)
                          ? 'text-primary-foreground/70 hover:text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                      aria-label={`More options for ${chat.title}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`chat-menu-trigger-${chat.id}`}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    onClick={(e) => e.stopPropagation()}
                    className="border-none shadow-elevated rounded-lg p-2 bg-card gap-0.5 min-w-0"
                  >
                    <DropdownMenuItem
                      onClick={() => handleRenameStart(chat)}
                      data-testid={`rename-chat-${chat.id}`}
                      className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                    >
                      <BorderColorIcon className="h-5 w-5" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteStart(chat)}
                      data-testid={`delete-chat-${chat.id}`}
                      className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                    >
                      <DeleteIcon className="h-5 w-5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        ))}
      </div>
      </TooltipProvider>
      {chatToDelete && (
        <DeleteChatDialog
          projectId={projectId}
          chat={chatToDelete}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDeleted={onDeleteChat}
        />
      )}
    </div>
  )
}

function ChatTitle({ title }: { title: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
  }, [title])

  if (!isTruncated) {
    return <span ref={ref} className="flex-1 truncate">{title}</span>
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={ref} className="flex-1 truncate">{title}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs break-words">{title}</TooltipContent>
    </Tooltip>
  )
}
